/* eslint-disable no-use-before-define */
import { LevelEditorCandyHelper } from './LevelEditorCandyHelper';

/**
 * Class for managing active LevelEditorDropZone columns
 */
export class LevelEditorDropZoneManager extends Phaser.Group {
  /**
   * constructor
   * @param {G.Board} board
   * @param {Object} importData
   */
  constructor(board, importData) {
    super(game);

    this._board = board;
    this.position = this._board.position;
    this.scale = this._board.scale;

    this.signals = {
      onDropModified: new Phaser.Signal(),
    };

    this._dropZones = [];
    this.import(importData);
  }

  /**
   * import level drop data
   * @param {Array} importData
   */
  import(importData) {
    if (!importData) return;
    this.update();
    importData.forEach((col, i) => {
      col.forEach((elem, index) => {
        this._dropZones[i].addElement(elem, index);
      });
    });
  }

  /**
   * export level drop data
   * @returns {Array}
   */
  export() {
    return this._dropZones.filter((dropZone) => dropZone !== null).map((dropZone) => dropZone.export());
  }

  /**
   * update drop zones
   */
  update() {
    const len = Math.max(this._dropZones.length, this._board.boardData.width);
    for (let i = 0; i < len; i++) {
      if (i < this._board.boardData.width) {
        if (!this._dropZones[i]) {
          const dropZone = new LevelEditorDropZone(this._board, i);
          this._dropZones[i] = this.add(dropZone);
          dropZone.signals.onDropModified.add(() => {
            this.signals.onDropModified.dispatch();
          });
        } else {
          this._dropZones[i].update();
        }
      } else if (this._dropZones[i]) {
        this._dropZones[i].destroy();
        this._dropZones[i] = null;
      }
    }
  }

  /**
   * remove all child elements from the dropZones
   */
  removeAllElements() {
    this._dropZones.forEach((dropZone) => { if (dropZone) dropZone.removeAllElements(); });
  }

  /**
   * get count of occurrence of a symbol type
   * @param {string} symbol
   * @returns {number}
   */
  countEditorSymbolOccurrence(symbol) {
    return this._dropZones.reduce((acc, dropZone) => acc + (dropZone ? dropZone.countEditorSymbolOccurrence(symbol) : 0), 0);
  }

  /**
   * get count of occurrence of a export string
   * @param {string} symbol
   * @returns {number}
   */
  countExportStringOccurrence(symbol) {
    return this._dropZones.reduce((acc, dropZone) => acc + (dropZone ? dropZone.countExportStringOccurrence(symbol) : 0), 0);
  }
}

/**
 * Class for Level Editor Drop zones columns
 */
class LevelEditorDropZone extends Phaser.Group {
  /**
   * constuctor
   * @param {G.Board} board
   * @param {number} col
   */
  constructor(board, col) {
    super(game);

    this._board = board;
    this._col = col;

    this._elements = [];
    this._elementsObj = [];

    this.x = col * this._board.tilesize;
    this.y = (this._getTopTile()) * this._board.tilesize;

    this.signals = {
      onDropModified: new Phaser.Signal(),
    };

    this._gfx = game.add.graphics();
    this._gfx.inputEnabled = true;
    this.add(this._gfx);
    this._redrawGfx();

    this._initKeys();
  }

  /**
   * init key listeners
   */
  _initKeys() {
    const { candyDataMgr } = G.Helpers;

    // place standard candy types in dropzone
    candyDataMgr.getAllBaseTypes()
      .filter((type) => type.editorKey)
      .forEach((type) => {
        const key = game.input.keyboard.addKey(Phaser.Keyboard[type.editorKey]);
        key.onDown.add(() => {
          const element = this.getElement(this._pointerToElementIndex());
          if (element && element.baseType.getEditorSymbol() === type.editorSymbol && element.baseType.changeForEditor) {
            element.baseType.changeForEditor();
            this.signals.onDropModified.dispatch();
          } else {
            this._onBtnPressed(type.id);
          }
        });
      });

    // loop through specials for candies in drop zone
    game.input.keyboard.addKey(Phaser.Keyboard.B).onDown.add(() => {
      const element = this.getElement(this._pointerToElementIndex());
      if (!element) return;
      LevelEditorCandyHelper.getInstance().loopThroughSpecials(element);
      this.signals.onDropModified.dispatch();
    });

    // apply candy status effects to drop zone candies
    candyDataMgr.getAllStatuses()
      .filter((status) => status.editorKey)
      .forEach((status) => {
        const key = game.input.keyboard.addKey(Phaser.Keyboard[status.editorKey]);
        key.onDown.add(() => {
          const element = this.getElement(this._pointerToElementIndex());
          if (!element) return;
          if (element.hasStatus()) {
            if (element.candyStatus.changeForEditor) element.candyStatus.changeForEditor();
            else element.removeStatus();
          } else element.setStatus(status);
          this.signals.onDropModified.dispatch();
        });
      });

    // remove last candy from the dropzone column
    const backSpaceKey = game.input.keyboard.addKey(Phaser.Keyboard.BACKSPACE);
    backSpaceKey.onDown.add(() => {
      if (this._gfx.input.pointerOver()) {
        this.removeLastElement();
        this.signals.onDropModified.dispatch();
      }
    });
    const TKey = game.input.keyboard.addKey(Phaser.Keyboard.T);
    TKey.onDown.add(() => {
      if (this._gfx.input.pointerOver()) {
        this.removeLastElement();
        this.signals.onDropModified.dispatch();
      }
    });
  }

  /**
   * export column data
   * @returns {Array}
   */
  export() {
    return this._elements.map((elem) => elem.export());
  }

  /**
   * on key pressed add a candy to the dropZone
   * @param {Object} elem target element
   */
  _onBtnPressed(elem) {
    const index = this._pointerToElementIndex();
    if (index !== null) {
      this.addElement(elem, index);
      this.signals.onDropModified.dispatch();
    }
  }

  /**
   * get the index of the element for the pointer position
   * @returns {number}
   */
  _pointerToElementIndex() {
    let index = null;
    if (this._gfx.input.pointerOver()) {
      index = Math.floor((this._gfx.worldPosition.y - game.input.activePointer.worldY) / (this._board.tilesize * this._board.scale.y));
    }
    return index;
  }

  /**
   * add a new element / candy to the dropZone
   * @param {Object} chunk
   * @param {number} index
   */
  addElement(chunk, index) {
    if (this._elements[index]) this._elements[index].destroy();
    const candy = new G.Candy(null, null, false);
    candy.init(0, 0, chunk);
    candy.position.setTo(this._board.tilesize * 0.5, (index + 0.5) * this._board.tilesize * -1);
    this.add(candy);
    this._elements[index] = candy;
    this._redrawGfx();
  }

  /**
   * get the element / candy at the specific index
   * @param {number} index
   * @returns {Object}
   */
  getElement(index) {
    return this._elements[index];
  }

  /**
   * remove the last element from the dropZone
   */
  removeLastElement() {
    if (this._elements.length > 0) this._elements.splice(-1, 1)[0].destroy();
    this._redrawGfx();
  }

  /**
   * remove all elements from the dropZone
   */
  removeAllElements() {
    while (this._elements.length > 0) this.removeLastElement();
  }

  /**
   * get top most tile index on the game board in the column
   * @returns {number}
   */
  _getTopTile() {
    for (let i = 0; i < this._board.boardData.height; i++) {
      if (this._board.isCellOnBoard(this._col, i)) return i;
    }
    return null;
  }

  /**
   * get count of symbol occurrence
   * @param {string} symbol
   * @returns {number}
   */
  countEditorSymbolOccurrence(symbol) {
    return this._elements.reduce((total, element) => total + (element ? element.hasEditorSymbol(symbol) : 0), 0);
  }

  /**
   * get count of symbol export string occurrence
   * @param {string} symbol
   * @returns {number}
   */
  countExportStringOccurrence(symbol) {
    return this._elements.reduce((total, element) => total + (element ? element.hasExportString(symbol) : 0), 0);
  }

  /**
   * redraw dropZone graphics
   */
  _redrawGfx() {
    const { tilesize } = this._board;
    this._gfx.clear();
    this._gfx.beginFill(0x0000ff, 0.5);
    const height = Math.max(1, this._elements.length + 1) * tilesize;
    this._gfx.drawRect(0, -height, tilesize, height);
  }

  /**
   * update dropZone
   */
  update() {
    // update column selection highlight
    const topTile = this._getTopTile();
    if (topTile === null) this.visible = false;
    else this.y = topTile * this._board.tilesize;
    // update candy highlight
    if (this._gfx.input.pointerOver()) this._gfx.alpha = 1;
    else this._gfx.alpha = 0.1;
  }
}
